(function () {
  const SECTION_SELECTOR = "[data-colour-mood-section]";

  function slug(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  }

  function getConfig(section) {
    const node = section.querySelector("[data-colour-mood-config]");
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error("Colour My Mood config could not be parsed.", error);
      return null;
    }
  }

  function calculateResult(config, selectedAnswers) {
    const scores = {};
    const activeResults = (config.results || []).filter((result) => result.active !== false);
    const order = activeResults.map((result) => result.id);
    const primaryWeight = Number(config.scoring && config.scoring.primaryWeight) || 2;
    const secondaryWeight = Number(config.scoring && config.scoring.secondaryWeight) || 1;

    order.forEach((id) => {
      scores[id] = 0;
    });

    selectedAnswers.forEach((answer) => {
      if (answer.primary && scores[answer.primary] !== undefined) scores[answer.primary] += primaryWeight;
      if (answer.secondary && scores[answer.secondary] !== undefined) scores[answer.secondary] += secondaryWeight;
    });

    const highestScore = Math.max(...Object.values(scores));
    const tiedColorIds = Object.keys(scores).filter((id) => scores[id] === highestScore);
    const firstQuestionPrimary = selectedAnswers[0] && selectedAnswers[0].primary;
    const finalQuestionPrimary = selectedAnswers[selectedAnswers.length - 1] && selectedAnswers[selectedAnswers.length - 1].primary;
    let winner =
      (tiedColorIds.includes(firstQuestionPrimary) && firstQuestionPrimary) ||
      (tiedColorIds.includes(finalQuestionPrimary) && finalQuestionPrimary) ||
      order.find((id) => tiedColorIds.includes(id));

    const overrideKey = selectedAnswers.map((answer) => answer.id).join("|");
    if (config.resultOverrides && config.resultOverrides[overrideKey]) {
      winner = config.resultOverrides[overrideKey];
    }

    return activeResults.find((result) => result.id === winner) || activeResults[0];
  }

  function initSection(section) {
    if (section.dataset.colourMoodInitialized === "true") return;

    const config = getConfig(section);
    if (!config || !config.questions || !config.results) return;

    section.dataset.colourMoodInitialized = "true";

    const intro = section.querySelector("[data-colour-mood-intro]");
    const quiz = section.querySelector("[data-colour-mood-quiz]");
    const resultView = section.querySelector("[data-colour-mood-result]");
    const startButtons = section.querySelectorAll("[data-colour-mood-start]");
    const questionTitle = section.querySelector("[data-colour-mood-question-title]");
    const answersNode = section.querySelector("[data-colour-mood-answers]");
    const homeButton = section.querySelector("[data-colour-mood-home]");
    const backButton = section.querySelector("[data-colour-mood-back]");
    const tryAgainButton = section.querySelector("[data-colour-mood-reset]");
    const progressLabel = section.querySelector("[data-colour-mood-progress-label]");
    const progressBar = section.querySelector("[data-colour-mood-progress-bar]");

    let step = 0;
    const selections = [];

    function setView(view) {
      if (intro) intro.hidden = view !== "intro";
      if (quiz) quiz.hidden = view !== "quiz";
      if (resultView) resultView.hidden = view !== "result";
    }

    function renderQuestion() {
      const question = config.questions[step];
      if (!question) return;

      section.classList.add("colour-my-mood--changing-question");
      questionTitle.textContent = question.text;
      progressLabel.textContent = `Question ${step + 1} of ${config.questions.length}`;
      progressBar.style.setProperty("--progress", `${((step + 1) / config.questions.length) * 100}%`);
      answersNode.innerHTML = "";

      question.answers.forEach((answer) => {
        const selectedAnswer = selections[step];
        const isSelected = selectedAnswer && selectedAnswer.questionId === question.id && selectedAnswer.id === answer.id;
        const button = document.createElement("button");
        button.type = "button";
        button.className = "colour-my-mood__answer";
        const icon = document.createElement("span");
        icon.className = "colour-my-mood__answer-icon";
        icon.setAttribute("aria-hidden", "true");
        icon.textContent = answer.icon || "";
        const label = document.createElement("span");
        label.className = "colour-my-mood__answer-label";
        label.textContent = answer.label;
        button.append(icon, label);
        button.setAttribute("aria-pressed", isSelected ? "true" : "false");
        button.addEventListener("click", () => {
          selections[step] = { ...answer, questionId: question.id };
          answersNode.querySelectorAll(".colour-my-mood__answer").forEach((node) => {
            node.setAttribute("aria-pressed", "false");
          });
          button.setAttribute("aria-pressed", "true");
          button.blur();
          window.setTimeout(() => {
            if (step < config.questions.length - 1) {
              step += 1;
              renderQuestion();
              return;
            }

            renderResult();
            setView("result");
          }, 180);
        });
        answersNode.appendChild(button);
      });

      backButton.hidden = step === 0;
      window.setTimeout(() => {
        section.classList.remove("colour-my-mood--changing-question");
      }, 80);
    }

    function renderResult() {
      const result = calculateResult(config, selections);
      if (!result) return;

      resultView.style.setProperty("--result-swatch", result.swatch || "#eeeeee");
      resultView.querySelector("[data-colour-mood-result-name]").textContent = result.name || "";
      resultView.querySelector("[data-colour-mood-result-tagline]").textContent = result.tagline || "";
      resultView.querySelector("[data-colour-mood-result-description]").textContent = result.description || "";
      resultView.querySelector("[data-colour-mood-product-title]").textContent = result.productTitle || "";
      resultView.querySelector("[data-colour-mood-product-price]").textContent = result.price || "";
      resultView.querySelector("[data-colour-mood-swatch-label]").textContent = result.name || "";

      const photoFrame = resultView.querySelector("[data-colour-mood-photo-frame]");
      photoFrame.style.setProperty("--colour-mood-image-position", result.imagePosition || "center center");
      photoFrame.innerHTML = "";

      if (result.image) {
        const image = document.createElement("img");
        image.className = "colour-my-mood__photo";
        image.src = result.image;
        image.alt = result.imageAlt || result.name || "";
        image.loading = "lazy";
        photoFrame.appendChild(image);
      } else {
        const placeholder = document.createElement("div");
        placeholder.className = "colour-my-mood__placeholder";
        placeholder.innerHTML = "<span></span>";
        photoFrame.appendChild(placeholder);
      }

      const cta = resultView.querySelector("[data-colour-mood-shop]");
      cta.textContent = config.shopButtonLabel || "Shop this colour";
      if (result.url) {
        cta.href = result.url;
        cta.removeAttribute("aria-disabled");
        cta.removeAttribute("role");
      } else {
        cta.removeAttribute("href");
        cta.setAttribute("role", "link");
        cta.setAttribute("aria-disabled", "true");
      }
    }

    startButtons.forEach((button) => {
      button.addEventListener("click", () => {
        step = 0;
        selections.length = 0;
        setView("quiz");
        renderQuestion();
      });
    });

    backButton.addEventListener("click", () => {
      if (step === 0) return;
      step -= 1;
      renderQuestion();
    });

    homeButton?.addEventListener("click", () => {
      step = 0;
      selections.length = 0;
      setView("intro");
    });

    tryAgainButton.addEventListener("click", () => {
      step = 0;
      selections.length = 0;
      setView("intro");
    });

    setView("intro");
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  document.addEventListener("DOMContentLoaded", initAll);
  document.addEventListener("shopify:section:load", initAll);
})();
